import * as React from "react";
import DOMPurify from "dompurify";
import { classNames } from "../../utils";
import "./textAreaBlock.scss";
import { AxiosResponse } from "axios";
import { useSelector } from "react-redux";
import * as AppTypes from "AppTypes";
import { checkLengthExceed } from "../../utils/validators";
import { dispatch } from "../../store";
import { appActions } from "../../actions";

interface ITextAreaBlockProps {
  inputId: string;
  title?: string;
  description?: string;
  value: string;
  maxLength: number;
  key?: string;
  warningText?: string;
  allowEmpty?: boolean;
  allowMaxLengthOnly?: boolean;
  firstLoad?: boolean;
  onSave?: (value: string) => void;
  onChange?: (value: string) => void;
  api?: (value: string) => Promise<AxiosResponse<any>>;
  apiCallBack?: (value: string) => void;
  style?: React.CSSProperties;
  placeholderText?: string;
}

interface IDataStore {
  [key: string]: {
    intervalId: number;
    currentContent: string;
    savedContent: string;
  };
}

const dataStore: IDataStore = {};

const TextAreaBlock: React.FC<ITextAreaBlockProps> = (props) => {
  const {
    inputId,
    title,
    description,
    maxLength,
    onSave,
    api,
    apiCallBack,
    style,
    placeholderText,
    onChange,
    key,
    warningText,
    allowMaxLengthOnly,
    allowEmpty = true,
    firstLoad = false,
  } = props;
  const [showWarning, setShowWarning] = React.useState(false);
  const [showWarningMaxLength, setShowWarningMaxLength] = React.useState(false);
  const [content, setContent] = React.useState(props.value);
  const [isSaved, setIsSaved] = React.useState(true);
  const [isModified, setIsModified] = React.useState(false);
  const inputElem = React.useRef<HTMLTextAreaElement>(null);

  const { langPack } = useSelector((store: AppTypes.ReducerState) => ({
    langPack: store.config.langPack,
  }));

  const resizeTextArea = () => {
    const elem = inputElem.current;
    if (!elem) return;
    elem.style.height = "0px";
    elem.style.height = elem.scrollHeight + 2 + "px";
  };

  React.useEffect(() => {
    dataStore[inputId] = dataStore[inputId] || {
      intervalId: 0,
      currentContent: content ?? "",
      savedContent: content ?? "",
    };
  }, [inputId]);

  React.useEffect(() => {
    resizeTextArea();
  }, [content]);

  React.useEffect(() => {
    if (!firstLoad) checkEmpty(content)
  }, []);

  const saveHandler = async (isLeaving?: boolean) => {
    const isTooLong = dataStore[inputId].currentContent.length > maxLength;
    // if (isLeaving) { console.log({ isTooLong, dataStore }) }
    if (isTooLong) return;

    if (api) {
      const response = await api(dataStore[inputId].currentContent);
      if (!(response && response.status === 200)) return;
      dataStore[inputId].savedContent = dataStore[inputId].currentContent;

      // update session storage
      if (apiCallBack) {
        apiCallBack(dataStore[inputId].currentContent);
      }

      // set state
      setIsSaved(true);
      if (isLeaving) {
        onSave(dataStore[inputId].currentContent);
      }

      dispatch(appActions.setApplicationUpdated(true));
    } else {
      onSave(dataStore[inputId].currentContent);
      setIsSaved(true);
    }

    if (dataStore[inputId].currentContent === dataStore[inputId].savedContent) {
      return setIsSaved(true);
    }
  };

  const checkMaxLength = (value: string) => {
    if (checkLengthExceed(value, maxLength)) {
      setShowWarningMaxLength(true);
      return true;
    } else {
      setShowWarningMaxLength(false);
      return false;
    }
  }

  const checkEmpty = (value: string) => {
    if (!allowEmpty && !value) {
      setShowWarning(true);
      return true;
    } else {
      setShowWarning(false);
      return false;
    }
  }

  const onFocus = () => {
    //dataStore[inputId].intervalId = window.setInterval(saveHandler, 3000);
  };

  const onBlur = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    //window.clearInterval(dataStore[inputId].intervalId);
    checkEmpty(event.target.value);

    // add delay to allow when other action triggered, will not prevent the action overrided by onBlur rerender
    setTimeout(() => {
      saveHandler(true);
    }, 1);
  };

  const onChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    let initialValue = event.target.value;
    let slicedValue;
    if (onChange) {
      onChange(event.target.value);
    }
    if (!isModified) setIsModified(true);
    slicedValue = allowMaxLengthOnly ? initialValue.slice(0, maxLength) : initialValue;
    setContent(slicedValue);
    setIsSaved(false);
    //onChange(dataStore[inputId].currentContent = event.target.value);
    dataStore[inputId].currentContent = slicedValue;

    checkEmpty(slicedValue)
    checkMaxLength(initialValue)
  };

  return (
    <div
      className={classNames({
        "text-area-block": true,
        "text-area-block--warning": showWarning || showWarningMaxLength,
      })}
      style={style ? style : {}}
    >
      <h3 className="dashboard__title">{title}</h3>
      <p
        className="dashboard__sub-title"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
      ></p>
      {showWarningMaxLength && (
        <p className="dashboard__sub-title error">{warningText}</p>
      )}
      <textarea
        ref={inputElem}
        key={key}
        onFocus={onFocus}
        onBlur={onBlur}
        className={classNames({
          "text-area-block__content": true,
          "text-area-block__content--overflow": showWarning || showWarningMaxLength,
        })}
        value={content}
        onChange={onChangeHandler}
        placeholder={placeholderText}
      ></textarea>

      <div className="text-area-block__counter">
        {dataStore[inputId] &&
          dataStore[inputId].currentContent &&
          (isSaved ? (
            <span className="app-active">{langPack["stateSaved"]}</span>
          ) : (
            <span style={{ opacity: 0.7 }}>{langPack["stateUnsaved"]}</span>
          ))}
        <span
          className={classNames({
            "dashboard__sub-title": true,
            "app-warning": showWarning || showWarningMaxLength,
          })}
        >
          {`(${content ? content.length : 0} / ${maxLength} ${langPack["labelChars"]})`}
        </span>
      </div>
    </div>
  );
};

export default TextAreaBlock;